import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useBarIndicatorsColors } from '@/composables/useBarIndicatorsColors';
import { Palette } from '@/themes/themeOverrides/Palette';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import { getActiveBarIndex } from '@/helpers/analytics/charts.js';
import { styled } from '@mui/material/styles';
import { analyticsStyles } from '@/themes/commonStyles/analytics';
import { useTranslate, useListContext } from 'react-admin';
import { DEFAULT_PERIOD } from '@/components/analytics/PeriodFilters.jsx';
import { breakpoints as themeBreakpoints } from '@/themes/themeOverrides/Breakpoints';

const colorGray = Palette.gray.gray300;
const colorRed = Palette.red.red800;
const colorOrange = Palette.yellow.yellow700;
const colorGreen = Palette.green.green500;

const StyledAnalyticsVerticalBars = styled(Box)(() => analyticsStyles.horizontalBars);

const DeviceTransactionsField = ({ record, className, onClick, defaultPeriod = DEFAULT_PERIOD }) => {
    const { filterValues } = useListContext();
    const currentPeriod = filterValues?.period ?? defaultPeriod;
    const analytics = record?.analytics?.[currentPeriod] ?? [];
    const translate = useTranslate();
    const transactionsNumber = formatNumber(analytics?.transactionsCount ?? 0, 0);
    const transactionsAvg = analytics?.transactionsAvg ?? 0;
    const transactionsStdDev = analytics?.transactionsStdDev ?? 0;
    const breakpoints = [transactionsAvg - transactionsStdDev, transactionsAvg, transactionsAvg + transactionsStdDev];
    const barColors = useBarIndicatorsColors(transactionsNumber, breakpoints, true);
    const percentDiff = transactionsAvg ? formatNumber(((transactionsNumber - transactionsAvg) / transactionsAvg) * 100, 0) : 0;

    if (!analytics) return null;

    let color;
    let text;
    const absPercentDiff = Math.abs(percentDiff);
    const activeBarIndex = getActiveBarIndex(transactionsNumber, breakpoints);

    if (transactionsNumber <= transactionsAvg - transactionsStdDev) {
        text = `${absPercentDiff}% ${translate('resources.product.labels.belowAverage')}`;
        color = colorRed;
    } else if (transactionsNumber > transactionsAvg - transactionsStdDev && transactionsNumber <= transactionsAvg) {
        text = `${absPercentDiff}% ${translate('resources.product.labels.belowAverage')}`;
        color = colorOrange;
    } else if (transactionsNumber > transactionsAvg && transactionsNumber <= transactionsAvg + transactionsStdDev) {
        text = `${absPercentDiff}% ${translate('resources.product.labels.aboveAverage')}`;
        color = colorGreen;
    } else {
        text = `${absPercentDiff}% ${translate('resources.product.labels.aboveAverage')}`;
        color = colorGreen;
    }

    return (
        <StyledAnalyticsVerticalBars className={className} sx={{ '&:hover .text-number': { textDecoration: 'underline' } }}>
            <Box onClick={onClick} className='bars-wrapper'>
                {barColors.map((color, i) => (
                    <Box
                        key={i}
                        className='bar'
                        sx={{
                            backgroundColor: i === activeBarIndex ? color : colorGray,
                        }}
                    />
                ))}
            </Box>
            <Box className='text-wrapper'>
                <Box
                    onClick={onClick}
                    className='text-number'
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '4px',
                        [`@media (max-width: ${themeBreakpoints.values.lg}px)`]: {
                            gap: '2px',
                            paddingTop: '0px !important',
                            position: 'relative',
                            top: '-2px',
                        },
                    }}
                >
                    <Box
                        component='span'
                        sx={{
                            display: 'none',
                            fontSize: '11px',
                            fontWeight: 400,
                            lineHeight: '16px',
                            letterSpacing: '-0.11px',
                            color: Palette.black,
                            [`@media (max-width: ${themeBreakpoints.values.lg}px)`]: {
                                display: 'inline',
                            },
                        }}
                    >
                        {translate('common.transactionsCountLabel')}:
                    </Box>
                    {transactionsNumber}
                </Box>
                <Box
                    onClick={onClick}
                    className='text-value'
                    sx={{
                        color: color,
                        [`@media (max-width: ${themeBreakpoints.values.lg}px)`]: {
                            fontSize: '11px !important',
                            lineHeight: '16px !important',
                            position: 'relative',
                            top: '-3px',
                        },
                    }}
                >
                    {text}
                </Box>
            </Box>
        </StyledAnalyticsVerticalBars>
    );
};

DeviceTransactionsField.propTypes = {
    record: PropTypes.shape({
        transactions: PropTypes.number,
    }),
    className: PropTypes.string,
    onClick: PropTypes.func,
};

export default DeviceTransactionsField;
